import { useEffect, useRef } from 'react'
import { Navigate, Outlet, useLocation } from 'react-router'
import type { JSX } from 'react'
import { useMutation } from '@/api/query'
import { useAuth } from '@/auth/context'
import { withNext } from '@/auth/routes'
import { useBrand } from '@/brand/context'
import { Organisations } from '@/pages/Organisations'
import { useTenants } from '@/tenant/context'
import { FullPageLoading, InlineError } from '@/ui/states'

/**
 * The shell exists only on an organisation's own host.
 *
 * On the apex a session is bound to no tenant, and `tenant_member` refuses
 * every tenant-scoped request from a host that is not the tenant's (spec:
 * *Hosts*) — so a shell rendered on `augrank.com` would be a sidebar of links
 * that all answer 401. What the apex offers a signed-in visitor instead is
 * `ApexLanding`: a handoff to the default organisation's host where one is
 * known, and only otherwise the organisation picker — the list of their
 * organisations, each *Open* a handoff of its own, and *New organisation* for
 * someone with none.
 *
 * Rendered in place rather than redirected to `/organisations`, as the brief
 * says: a bookmarked `augrank.com/dashboard` lands at that address, and it is
 * `ApexLanding` — the handoff, or the picker — that is shown there rather than
 * a redirect to a different path.
 *
 * `NoWorkspace` handles the truly unowned host (`brand.host === 'unknown'`,
 * spec: *Hosts*) at the root of the route tree, ahead of every band, so this
 * component is never reached for it.
 *
 * Sits inside `RequireAuth`, which already handles `loading` and `anonymous`;
 * the two branches here are for the case where it does not.
 */
export function RequireTenantHost(): JSX.Element {
  const brand = useBrand()
  const { status } = useAuth()
  const location = useLocation()

  if (brand.host === 'tenant') return <Outlet />
  if (status === 'loading') return <FullPageLoading />
  if (status === 'anonymous') {
    return <Navigate to={withNext('/login', `${location.pathname}${location.search}`)} replace />
  }
  return <ApexLanding />
}

/**
 * What a signed-in visitor on the apex sees before the picker.
 *
 * Spec *Landing*, and the owner's own words on `select-default`'s: *"Multi-org
 * → the default org, no picker."* `useTenants().defaultTenantId` is already
 * the answer — resolved from `GET /api/v1/tenants` the moment the list is
 * read — so the handoff here is a plain `select()` (`TenantProvider`) rather
 * than a second way of asking the same question: the same request
 * `Organisations`' own *Open* button sends, spent automatically instead of on
 * a click.
 *
 * **Attempted once per id, not once ever** (`attempted`): a ref rather than
 * `useMutation`'s own `pending`/`state`, because a failed attempt must not
 * retry itself into a loop on every render, and a *different* default
 * arriving later (someone changed it in another tab) is a fresh question the
 * ref lets back in.
 *
 * The picker still renders underneath every branch that is not "handoff in
 * flight or already left" — no default organisation at all, or this one
 * refused — with the failure surfaced above it through the same `InlineError`
 * the manual *Open* uses, so a refusal reads the same way whichever button
 * asked for it.
 */
function ApexLanding(): JSX.Element {
  const { status, defaultTenantId, select } = useTenants()
  const { run: mintHandoff, state: handoffState } = useMutation((tenantId: string) =>
    select(tenantId),
  )
  const attempted = useRef<string | null>(null)

  useEffect(() => {
    if (status !== 'ready' || !defaultTenantId) return
    if (attempted.current === defaultTenantId) return
    attempted.current = defaultTenantId
    void mintHandoff(defaultTenantId)
  }, [status, defaultTenantId, mintHandoff])

  // Loading while the list is still being read, and while a default is known
  // and has not (yet) failed to hand off — which covers the attempt in
  // flight and the moment right after it succeeds, when the only thing left
  // to do is wait for the browser to leave.
  if (status === 'loading' || (defaultTenantId && !handoffState.failure)) {
    return <FullPageLoading label="Opening your workspace" />
  }

  return (
    <>
      <InlineError failure={handoffState.failure} />
      <Organisations />
    </>
  )
}
